import type {
  SecurityControlAggregate,
  SecurityControlMetrics,
} from "@app/deploy/security-controls";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
  DataTableSort,
} from "@app/ui/shared/datatable";
import { getStackImg } from "@app/ui/shared/stack";
import { tokens } from "@app/ui/shared/tokens";
import { Tooltip } from "@app/ui/shared/tooltip";
import cn from "classnames";
import { type MouseEvent, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ComplianceSummary } from "./compliance-summary";
import {
  ControlsDataTable,
  type ControlsDataTableHeaderComponentParams,
} from "./controls-data-table";
import type { ScopeEnvironment } from "./scope-sidebar";
import { CONTROLS_EVENTS, controlsEvent } from "./tuna-events";

export interface ControlTableProps {
  controls: SecurityControlAggregate[];
  scopeEnvironments: ScopeEnvironment[];
  isAggregateScope: boolean;
  isLoading: boolean;
  selectedControlId?: string;
  getControlLink: (controlId: string) => string;
  /**
   * Sort from URL (including legacy params before migration).
   * Pass `null` when controlsSortBy is invalid for the current scope.
   */
  initialSort?: DataTableSort | null;
  className?: string;
}

const STACK_HEADER_COPY = {
  dedicated: {
    label: "Dedicated stack",
    tooltip:
      "This environment is on a dedicated stack. All controls are available.",
  },
  self_hosted: {
    label: "Self-hosted stack",
    tooltip:
      "This environment is on a self-hosted stack running in your AWS account. Dedicated-stack controls are available.",
  },
  shared: {
    label: "Shared stack",
    tooltip:
      "This environment is in a shared stack. Shared stacks are not designed for production, so some controls are not available.",
  },
} as const;

const preventRowClickNavigation = (event: MouseEvent) => {
  event.stopPropagation();
};

const formatFrameworks = (frameworks: { displayName: string }[]) => {
  if (frameworks.length === 0) {
    return "All Frameworks";
  }
  return frameworks.map((f) => f.displayName).join(", ");
};

/** Link-wrapped so badge clicks navigate like the rest of the row. */
const PremiumBadge = ({ to }: { to: string }) => (
  <Tooltip text="Enhanced reporting and export for this control is available as an add-on. Contact support to enable it.">
    <Link
      to={to}
      onClick={preventRowClickNavigation}
      className="relative z-20 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700 pointer-events-auto"
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-purple-500" />
      Add-on available
    </Link>
  </Tooltip>
);

const DEDICATED_STACKS_ONLY_METRICS: SecurityControlMetrics = {
  totalInScope: 0,
  totalCompliant: 0,
  totalIgnored: 0,
  resourceType: "Resource",
  lastEvaluation: "",
  lastEvaluationInWords: "",
  progressPercentage: 0,
  status: "NOT_STARTED",
  implementationSummary:
    "Available in production environments on dedicated or self-hosted stacks",
  implementationAsString: "0%",
  statusSort: 0,
};

const getHeaderStackType = (env: ScopeEnvironment) => {
  if (env.stackType) return env.stackType;
  return env.type === "production" ? "dedicated" : "shared";
};

const EnvironmentTypeIcon = ({ env }: { env: ScopeEnvironment }) => {
  const stackType = getHeaderStackType(env);
  const copy = STACK_HEADER_COPY[stackType];
  return (
    <Tooltip text={copy.tooltip}>
      <img
        src={getStackImg(stackType)}
        alt={copy.label}
        className="h-3.5 w-3.5 shrink-0"
      />
    </Tooltip>
  );
};

const getEnvironmentHeaderParams = (
  env: ScopeEnvironment,
): ControlsDataTableHeaderComponentParams => ({
  leadingIcon: <EnvironmentTypeIcon env={env} />,
  labelClassName: "truncate",
});

const EnvironmentMetricsCell = ({
  control,
  envId,
  isLoading,
}: {
  control: SecurityControlAggregate;
  envId: string;
  isLoading: boolean;
}) => {
  if (control.globalControl.isOrganizationScoped) {
    return (
      <div className="flex min-w-0 items-center">
        <ComplianceSummary
          metrics={control.cumulativeMetrics}
          isLoading={isLoading}
        />
      </div>
    );
  }

  const customerControl = control.customerControlsByEnvironmentId[envId];

  if (!customerControl) {
    if (isLoading) {
      return (
        <div className="flex min-w-0 items-center">
          <ComplianceSummary
            metrics={control.cumulativeMetrics}
            isLoading={true}
          />
        </div>
      );
    }
    return <span className="text-sm text-gray-500">Not evaluated yet</span>;
  }

  if (customerControl.isDisabled) {
    return (
      <div className="flex min-w-0 items-center">
        <ComplianceSummary metrics={DEDICATED_STACKS_ONLY_METRICS} />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center">
      <ComplianceSummary
        metrics={customerControl.metrics}
        isLoading={isLoading}
      />
    </div>
  );
};

const getEnvironmentMetricsSort = (
  control: SecurityControlAggregate | undefined,
  envId: string,
) => {
  if (!control) return 0;
  if (control.globalControl.isOrganizationScoped) {
    return control.cumulativeMetrics.statusSort;
  }
  const customerControl = control.customerControlsByEnvironmentId[envId];
  if (!customerControl) return -1;
  if (customerControl.isDisabled) return 0;
  return customerControl.metrics.statusSort;
};

const ControlNameCell = ({
  control,
  href,
}: {
  control: SecurityControlAggregate;
  href: string;
}) => {
  const { globalControl } = control;

  return (
    <div className="min-w-0 w-full pr-2">
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <Link
          to={href}
          onClick={preventRowClickNavigation}
          className={`${tokens.type["table link"]} min-w-0 break-words`}
        >
          <span className={`${tokens.type.darker} break-words`}>
            {globalControl.name}
          </span>
        </Link>
        {globalControl.isPremium ? (
          <span className="inline-flex shrink-0">
            <PremiumBadge to={href} />
          </span>
        ) : null}
      </div>
      <div className={`mt-1 break-words ${tokens.type["normal lighter"]}`}>
        <span>{globalControl.area}</span>
        <span className="mx-2 text-gray-400">•</span>
        <span>{formatFrameworks(globalControl.frameworks)}</span>
      </div>
    </div>
  );
};

const getControlRowId = (data: SecurityControlAggregate) => data.id;

export const ControlTable = ({
  controls,
  scopeEnvironments,
  isAggregateScope,
  isLoading,
  selectedControlId,
  getControlLink,
  initialSort,
  className = "",
}: ControlTableProps) => {
  const navigate = useNavigate();
  const handleRowClick = useCallback(
    (control: SecurityControlAggregate) => {
      controlsEvent(CONTROLS_EVENTS.CONTROL_OPENED, {
        controlId: control.id,
        controlName: control.globalControl.name,
        controlArea: control.globalControl.area,
      });
      navigate(getControlLink(control.id));
    },
    [getControlLink, navigate],
  );
  const getControlRowClassName = useCallback(
    (control: SecurityControlAggregate) =>
      selectedControlId === control.id ? "control-row-selected" : undefined,
    [selectedControlId],
  );
  const statusColumnId = isAggregateScope
    ? "cumulativeStatus"
    : scopeEnvironments.length === 1
      ? `env-${scopeEnvironments[0].id}`
      : undefined;

  const defaultSort = useMemo(
    () =>
      statusColumnId
        ? ({ field: statusColumnId, direction: "asc" } as const)
        : undefined,
    [statusColumnId],
  );

  const getControlLinkRef = useRef(getControlLink);
  getControlLinkRef.current = getControlLink;

  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const colDefs = useMemo(() => {
    const columns: DataTableColDef<SecurityControlAggregate>[] = [
      {
        colId: "name",
        headerName: "Control",
        flex: 2,
        minWidth: 280,
        wrapText: true,
        cellClass: "control-name-cell",
        valueGetter: ({ data }) => data?.globalControl.name ?? "",
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<SecurityControlAggregate>) => {
          if (!data) return null;
          return (
            <ControlNameCell
              control={data}
              href={getControlLinkRef.current(data.id)}
            />
          );
        },
      },
    ];

    if (isAggregateScope) {
      columns.push({
        colId: "cumulativeStatus",
        headerName: "Cumulative Status",
        flex: 1,
        minWidth: 160,
        cellClass: "control-status-cell",
        valueGetter: ({ data }) => data?.cumulativeMetrics.statusSort ?? 0,
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<SecurityControlAggregate>) => {
          if (!data) return null;
          return (
            <div className="flex min-w-0 items-center">
              <ComplianceSummary
                metrics={data.cumulativeMetrics}
                isLoading={isLoadingRef.current}
              />
            </div>
          );
        },
      });
    }

    for (const env of scopeEnvironments) {
      columns.push({
        colId: `env-${env.id}`,
        headerName: env.handle,
        headerComponentParams: getEnvironmentHeaderParams(env),
        flex: 1,
        minWidth: 160,
        cellClass: "control-status-cell",
        valueGetter: ({ data }) => getEnvironmentMetricsSort(data, env.id),
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<SecurityControlAggregate>) => {
          if (!data) return null;
          return (
            <EnvironmentMetricsCell
              control={data}
              envId={env.id}
              isLoading={isLoadingRef.current}
            />
          );
        },
      });
    }

    return columns;
  }, [isAggregateScope, scopeEnvironments]);

  if (!isLoading && controls.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-600 shadow-sm">
        No controls match the current filters.
      </div>
    );
  }

  return (
    <ControlsDataTable<SecurityControlAggregate>
      rowData={controls}
      colDefs={colDefs}
      getRowId={getControlRowId}
      onRowClick={handleRowClick}
      getRowClassName={getControlRowClassName}
      isLoading={isLoading && controls.length === 0}
      className={cn("control-table-grid", className)}
      defaultSort={defaultSort}
      initialSort={initialSort}
    />
  );
};
